import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import BankTransferPayment from '../../components/payments/BankTransferPayment';
import PaymentMethodSelector from '../../components/payments/PaymentMethodSelector';
import RediPayPayment from '../../components/payments/RediPayPayment';
import WalletPayment from '../../components/payments/WalletPayment';
import { burialPlotBookingService } from '../../services/burial-plot-booking.service';
import { useAuth } from '../../hooks/useAuth';

const BurialPlotPaymentPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // AIAHAD INTEGRATION: Payment method selection state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'BANK_TRANSFER' | 'AIAHAD' | 'WALLET'>('AIAHAD');

  // Get state from navigation if available
  const state = location.state as {
    booking?: any;
    pricing?: any;
    selectedPlot?: any;
  };

  useEffect(() => {
    if (!bookingId) {
      setError('Invalid booking ID');
      setIsLoading(false);
      return;
    }

    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      // If we have booking data from state, use it first
      if (state?.booking) {
        setBookingDetails(state.booking);

        // Check if payment is already completed
        if (state.booking.payment?.status === 'VERIFIED' || state.booking.payment?.status === 'COMPLETED') {
          toast.success('Payment already completed!');
          setTimeout(() => {
            navigate(`/bookings/burial-plot/${bookingId}`, { replace: true });
          }, 2000);
        }
      } else {
        // If no state data, fetch booking details from API
        console.log('🔄 Fetching booking details from API for bookingId:', bookingId);
        const bookingResponse = await burialPlotBookingService.getBookingDetails(bookingId);

        if (bookingResponse.success && bookingResponse.data) {
          setBookingDetails(bookingResponse.data);
          console.log('✅ Booking details loaded successfully:', bookingResponse.data);

          // Check if payment is already completed
          if (bookingResponse.data.payment?.status === 'VERIFIED' || bookingResponse.data.payment?.status === 'COMPLETED') {
            toast.success('Payment already completed!');
            setTimeout(() => {
              navigate(`/bookings/burial-plot/${bookingId}`, { replace: true });
            }, 2000);
          }
        } else {
          throw new Error(bookingResponse.message || 'Booking not found');
        }
      }
    } catch (error: any) {
      console.error('Failed to load booking details:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentData: any) => {
    toast.success('Payment submitted successfully! Your payment is now pending verification.');

    // Navigate to burial plot booking details after successful payment
    setTimeout(() => {
      navigate(`/bookings/burial-plot/${bookingId}`, {
        replace: true,
        state: { paymentCompleted: true }
      });
    }, 2000);
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment error:', error);
    toast.error('Payment submission failed');
  };

  const handlePaymentCancel = () => {
    // Show confirmation message
    toast.success('Payment can be completed later. Your booking details have been saved.');

    // Navigate to burial plot booking details page where they can complete payment later
    setTimeout(() => {
      navigate(`/bookings/burial-plot/${bookingId}`, { replace: true });
    }, 1500);
  };

  const handleBackToBookings = () => {
    navigate('/bookings');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (error || !bookingDetails) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-red-400 mb-4">
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Booking</h3>
            <p className="text-gray-500 mb-4">{error || 'Booking details not found'}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                Complete Payment
              </h1>
              <p className="mt-2 text-sm text-neutral-600">
                Complete your burial plot booking payment
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 border border-neutral-200 sticky top-4">
              <h2 className="text-lg font-medium text-neutral-900 mb-4">Booking Summary</h2>

              {/* Plot Details */}
              {state?.selectedPlot && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-neutral-700 mb-3">Selected Plot</h3>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plot Number:</span>
                        <span className="font-medium">{state.selectedPlot.plotNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Section:</span>
                        <span className="font-medium">{state.selectedPlot.section}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Row:</span>
                        <span className="font-medium">{state.selectedPlot.row}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Size:</span>
                        <span className="font-medium">{state.selectedPlot.plotSize}</span>
                      </div>
                      {state.selectedPlot.dimensions && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Dimensions:</span>
                          <span className="font-medium">
                            {state.selectedPlot.dimensions.length}m × {state.selectedPlot.dimensions.width}m
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Burial Details */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Burial Details</h3>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Burial Size:</span>
                    <span className="font-medium">{bookingDetails.burialSize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cemetery:</span>
                    <span className="font-medium">{bookingDetails.burialArea}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Urgency:</span>
                    <span className="font-medium">{bookingDetails.urgencyLevel}</span>
                  </div>
                  {bookingDetails.scheduledDateTime && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scheduled Date:</span>
                      <span className="font-medium">
                        {new Date(bookingDetails.scheduledDateTime).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {bookingDetails.specialRequests && (
                    <div className="mt-2">
                      <span className="text-gray-600">Special Requests:</span>
                      <p className="font-medium mt-1">{bookingDetails.specialRequests}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              {state?.pricing && (
                <div>
                  <h3 className="text-sm font-medium text-neutral-700 mb-3">Payment Details</h3>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Price:</span>
                      <span>RM {state.pricing.basePrice}</span>
                    </div>
                    {state.pricing.urgencySurcharge > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Urgency Surcharge:</span>
                        <span>RM {state.pricing.urgencySurcharge}</span>
                      </div>
                    )}
                    <div className="border-t pt-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-neutral-900">Total Amount:</span>
                        <span className="text-lg font-bold text-blue-600">RM {state.pricing.total}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">
                        {state.pricing.breakdown}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Booking Reference */}
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-gray-500">
                  <div>Booking ID: {bookingDetails.id}</div>
                  <div>Transfer Reference: {bookingDetails.transferReference}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Selection and Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-neutral-200 p-6">
              {/* Payment Method Selector */}
              <PaymentMethodSelector
                selectedMethod={selectedPaymentMethod}
                onMethodChange={setSelectedPaymentMethod}
                amount={state?.pricing?.total || bookingDetails.pricing?.total}
              />

              {/* Payment Form - Conditional Rendering */}
              {selectedPaymentMethod === 'BANK_TRANSFER' && (
                <BankTransferPayment
                  bookingId={bookingId}
                  amount={state?.pricing?.total || bookingDetails.pricing?.total}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  onPaymentCancel={handlePaymentCancel}
                  bookingData={{
                    providerName: state?.selectedPlot?.providerName || bookingDetails.providerName || 'Burial Service Provider',
                    caseNumber: bookingDetails.caseNumber,
                    tripType: 'burial-plot',
                    ambulanceType: 'burial-plot'
                  }}
                />
              )}

              {selectedPaymentMethod === 'WALLET' && (
                <WalletPayment
                  amount={state?.pricing?.total || bookingDetails.pricing?.total || 2.00}
                  description={`Burial plot booking payment - ${bookingDetails.burialArea} - ${bookingDetails.burialSize}`}
                  referenceNo={bookingDetails.id}
                  itemName={`Burial Plot - ${state?.selectedPlot?.plotNumber || bookingDetails.burialArea}`}
                  onPaymentCompleted={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              )}

              {selectedPaymentMethod === 'AIAHAD' && (
                <RediPayPayment
                  amount={state?.pricing?.total || bookingDetails.pricing?.total || 2.00}
                  description={`Burial plot booking payment - ${bookingDetails.burialArea} - ${bookingDetails.burialSize}`}
                  referenceNo={bookingDetails.id}
                  itemName={`Burial Plot - ${state?.selectedPlot?.plotNumber || bookingDetails.burialArea}`}
                  callbackUrl={`${import.meta.env.VITE_API_URL}/api/redipay/callback`}
                  userEmail={user?.email || bookingDetails.contactEmail || 'user@test.com'} // Use current user email first, then booking email, then fallback
                  userName={`${user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : bookingDetails.contactName || ''}`.trim() || 'Test User'} // Use current user name first, then booking name, then fallback
                  userPhone={user?.phone?.replace(/^\+/, '') || bookingDetails.contactPhone?.replace(/^\+/, '') || '0123456789'} // Remove + prefix for AiAhad validation; digits-only +60/+65 numbers both flow safely
                  caseId={undefined} // AIAHAD INTEGRATION: Burial plots typically don't have cases
                  userId={undefined} // AIAHAD INTEGRATION: Let component extract from JWT token
                  onPaymentCreated={(paymentData) => {
                    console.log('AiAhad payment created:', paymentData);
                    // Payment creation is handled by AiAhad component with redirect
                  }}
                  onPaymentError={handlePaymentError}
                />
              )}
            </div>

            {/* Important Information */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Important Information</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Payment verification typically takes 2-4 business hours</li>
                <li>• Please keep your payment receipt for verification</li>
                <li>• Your selected plot will be reserved for 24 hours</li>
                <li>• Contact support if you don't receive confirmation within 4 hours</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurialPlotPaymentPage;